import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useMediaQuery, useStorage } from '@vueuse/core'
import { useDeckStore } from '@/store'
import { useUserStore } from '@/store/user'
import useInstallApp from '@/composables/useInstallApp'

type NudgeKey =
  'addColumn' | 'aiDiscovery' | 'mobileInstall' | 'desktopCross' | 'mobileCross'

interface OnboardingState {
  aiDiscovered: boolean
  dismissed: Record<NudgeKey, boolean>
}

const DEFAULT_STATE: OnboardingState = {
  aiDiscovered: false,
  dismissed: {
    addColumn: false,
    aiDiscovery: false,
    mobileInstall: false,
    desktopCross: false,
    mobileCross: false
  }
}

// Mémoire minimale : dismissals + flag "a déjà ouvert /chat", pas de tracking de sessions.
const state = useStorage<OnboardingState>(
  'onboarding',
  DEFAULT_STATE,
  undefined,
  { mergeDefaults: true }
)

function dismissNudge(key: NudgeKey): void {
  state.value.dismissed = { ...state.value.dismissed, [key]: true }
}

export function useOnboardingNudges() {
  const route = useRoute()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const { columns, columnsReady } = storeToRefs(useDeckStore())
  const { aiEnabled } = storeToRefs(useUserStore())

  const { canInstall, isStandalone, prompt: promptInstall } = useInstallApp()

  watch(
    () => route.name,
    name => {
      if (typeof name === 'string' && name.startsWith('ai')) {
        state.value.aiDiscovered = true
      }
    },
    { immediate: true }
  )

  const showAddColumnNudge = computed(
    () =>
      !state.value.dismissed.addColumn &&
      columnsReady.value &&
      columns.value.length === 1
  )

  const showAiDiscoveryNudge = computed(
    () =>
      !state.value.dismissed.aiDiscovery &&
      aiEnabled.value &&
      !state.value.aiDiscovered &&
      columnsReady.value &&
      columns.value.length >= 1
  )

  const showMobileInstallNudge = computed(
    () =>
      !state.value.dismissed.mobileInstall &&
      isMobile.value &&
      !isStandalone.value &&
      canInstall.value
  )

  // Proposé sans vérifier si l'autre device a servi : moins précis mais plus simple.
  const showDesktopCrossDeviceNudge = computed(
    () =>
      !state.value.dismissed.desktopCross &&
      !isMobile.value &&
      columnsReady.value &&
      columns.value.length >= 1
  )

  const showMobileCrossDeviceNudge = computed(
    () =>
      !state.value.dismissed.mobileCross &&
      isMobile.value &&
      columnsReady.value &&
      columns.value.length >= 1
  )

  return {
    showAddColumnNudge,
    showAiDiscoveryNudge,
    showMobileInstallNudge,
    showDesktopCrossDeviceNudge,
    showMobileCrossDeviceNudge,
    dismiss: dismissNudge,
    promptInstall
  }
}
